/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repository.notification

import kotlinx.coroutines.flow.first
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.model.dashboard.PatientStatus
import studio.oddpods.cauce.core.model.notification.NotificationType
import studio.oddpods.cauce.core.model.notification.ScheduledNotification
import kotlin.time.Clock

class NotificationSchedulingService(
    private val dashboardRepository: DashboardRepository,
    private val notificationScheduler: NotificationScheduler,
    private val notificationSettingsRepository: NotificationSettingsRepository,
) {

    suspend fun reschedulePaymentReminders() {
        val prefs = notificationSettingsRepository.preferences.first()
        if (!prefs.paymentRemindersEnabled || !notificationScheduler.hasPermission()) {
            notificationScheduler.cancelAll()
            return
        }

        notificationScheduler.cancelAll()

        val triggerMillis = nextTriggerMillis(
            targetDayOfWeek = prefs.paymentReminderDayOfWeek,
            targetHour = prefs.paymentReminderHour,
            targetMinute = prefs.paymentReminderMinute,
        )

        val now = Clock.System.now()
        val tz = TimeZone.currentSystemDefault()
        val currentMonth = now.toLocalDateTime(tz).let { dt ->
            "${dt.year}-${dt.month.number.toString().padStart(2, '0')}"
        }

        val summary = dashboardRepository.getDashboard(currentMonth).first().getOrNull()

        val patientsWithDebt = summary?.patients
            ?.filter { it.status == PatientStatus.OVERDUE || it.totalOwed > 0 }
            ?: emptyList()

        if (patientsWithDebt.isEmpty()) return

        val title = "Recordatorios de pago"
        val body = when (patientsWithDebt.size) {
            1 -> "${patientsWithDebt.first().patientName} tiene un saldo pendiente"
            else -> "${patientsWithDebt.size} pacientes tienen saldos pendientes"
        }

        notificationScheduler.schedule(
            ScheduledNotification(
                id = PAYMENT_REMINDER_ID,
                type = NotificationType.PAYMENT_REMINDER,
                title = title,
                body = body,
                triggerAtMillis = triggerMillis,
            ),
        )
    }

    fun cancelAll() {
        notificationScheduler.cancelAll()
    }

    companion object {
        private const val PAYMENT_REMINDER_ID = 1000

        internal fun nextTriggerMillis(
            targetDayOfWeek: Int,
            targetHour: Int,
            targetMinute: Int,
        ): Long {
            require(targetDayOfWeek in 1..7) { "dayOfWeek must be 1..7 (ISO), got $targetDayOfWeek" }
            require(targetHour in 0..23) { "hour must be 0..23, got $targetHour" }
            require(targetMinute in 0..59) { "minute must be 0..59, got $targetMinute" }

            val tz = TimeZone.currentSystemDefault()
            val now = Clock.System.now()
            val today = now.toLocalDateTime(tz).date
            val targetDay = DayOfWeek(targetDayOfWeek)
            val todayDow = today.dayOfWeek

            val daysUntil = (targetDay.ordinal - todayDow.ordinal + 7) % 7
            val targetDate = if (daysUntil == 0) {
                val targetTime = LocalTime(targetHour, targetMinute)
                val candidateInstant = today.atTime(targetTime).toInstant(tz)
                if (candidateInstant > now) today else today.plus(7, DateTimeUnit.DAY)
            } else {
                today.plus(daysUntil, DateTimeUnit.DAY)
            }

            return targetDate
                .atTime(LocalTime(targetHour, targetMinute))
                .toInstant(tz)
                .toEpochMilliseconds()
        }
    }
}
